import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import Button from '~/components/Buttons/Button'
import ContainerHomGradientBorder from '~/components/Containers/ContainerHomGradientBorder'
import ContainerHomGradientBorderHexagon from '~/components/Containers/ContainerHomGradientBorderHexagon'
import useGetQuestsForArticle from '~/gamification/hooks/useGetQuestsForArticle'
import { Checkbox } from '~/components/Checkbox'
import { EventStatus, REWARD_EVENT, REWARD_FEATURE } from '~/gamification/types/gamification.types'
import { MultipleChoiceTestEventScore } from '~/types/multiple-choice'
import { TentativeEvent } from '~/homebase/types/Homebase.types'
import Tex from '~/components/Tex'
import { FileTopic, TopicData } from '~/types/theory'
import { displayMultipleChoiceTestActionCreator, selectDisplayMultipleChoiceTest } from '~/redux/slices/theory'
import { resetMultipleChoiceTestActionCreator } from '~/redux/slices/multiple-choice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import {
  clearLearningContextActionCreator,
  setLearningPositionActionCreator,
} from '~/aioracle/slices/learning-context.slice'
import { buildEncyclopediaContext } from '~/encyclopedia/utils/learning-context.utils'
import { useGetMultipleChoiceTestQuery } from '~/redux/apis/multiple-choice-test-api'

import { isAuthenticated } from '~/auth/slices/auth.slice'
import ArticleNavigationCard from '~/shared/components/Cards/ArticleNavigationCard'
import MultipleChoiceTestSchema from '~/components/Metadata/Schema/MultipleChoiceTestSchema'
import ArticleSchema from '~/components/Metadata/Schema/ArticleSchema'
import mathEssentialsAnalytics from '~/analytics/math-essentials/mathEssentialsAnalytics'
import { useGetBaseSrcKey } from '~/hooks/useGetBaseSrcKey'
import { useUpdateEventDataMutation } from '~/redux/apis/reward-api'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import {
  useGetEncyclopediaProgressQuery,
  useSetEncyclopediaProgressMutation,
} from '~/curriculum/apis/curriculum-progress.api'
import { selectEvents } from '~/gamification/slices/gamification.slice'
import { getMultipleChoiceThreshold } from '~/encyclopedia/utils/encyclopedia.utils'
import { useSetUpdateMultChoiceTestProgressMutation } from '~/redux/apis/activity-api'
import { stripPromoBoxes } from '~/utils/stripPromoBoxes'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '60vw' }]

const MultipleChoiceTest = dynamic(() => import('~/encyclopedia/components/MultipleChoiceTest/MultipleChoiceTest'), {
  ssr: false,
})

function getTopicSequenceData(topic: FileTopic): {
  nextTopic?: TopicData
  lastTopic?: TopicData
  indexOfTopic: number
} {
  const indexOfTopic = topic.siblings.findIndex((sibling) => sibling.path === topic.path)
  return { lastTopic: topic.siblings[indexOfTopic - 1], nextTopic: topic.siblings[indexOfTopic + 1], indexOfTopic }
}

export default function TheoryArticleView({
  articleBody,
  topic,
  hideHeading,
  installsLearningContext,
  isCurriculumLevel,
}: {
  articleBody: string
  topic: FileTopic
  hideHeading?: boolean
  /** Opt-in gate, passed only by `Theory.tsx`: this component also renders for drill and bootcamp
   *  video. A prop rather than `isEncyclopediaPage` below, whose substring test hits query strings. */
  installsLearningContext?: boolean
  isCurriculumLevel?: boolean
}): React.ReactElement | null {
  const router = useRouter()
  const { t } = useTranslation(['encyclopedia', 'homebase'])
  const authenticated = useAppSelector(isAuthenticated)
  const baseIcon = useGetBaseSrcKey()
  const dispatch = useAppDispatch()
  const [setEncyclopediaProgress] = useSetEncyclopediaProgressMutation()
  const [setMultChoiceProgress] = useSetUpdateMultChoiceTestProgressMutation()
  const { path, htmlContent, cssUrl, h1 } = topic
  const { nextTopic, lastTopic, indexOfTopic } = getTopicSequenceData(topic)
  const { isLoading, parentQuestId, parentQuest } = useGetQuestsForArticle(topic)

  const isCurriculumPage = router.query.showGrade === 'true'
  const isEncyclopediaPage = router.asPath.includes('encyclopedia') && !router.asPath.includes('curriculum')

  // Install and clear are two effects whose dep lists must stay in sync: an article -> article
  // navigation swaps `path` under the same instance. The token is `path`; `topic.key` is cross-locale.
  useEffect(() => {
    if (!installsLearningContext) return
    const position = buildEncyclopediaContext({ path })
    if (position) dispatch(setLearningPositionActionCreator(position))
  }, [installsLearningContext, path, dispatch])

  useEffect(() => {
    if (!installsLearningContext) return undefined
    return () => {
      dispatch(clearLearningContextActionCreator({ contextId: path }))
    }
  }, [installsLearningContext, path, dispatch])

  const curriculumProgressData = useGetEncyclopediaProgressQuery({ path }, { skip: !isCurriculumPage })?.data
  const isCompleted = curriculumProgressData?.completed
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const [isEncyclopediaRead, setEncyclopediaRead] = useState(!!isCompleted)
  const { data: multipleChoiceTest } = useGetMultipleChoiceTestQuery({
    url: path,
  })
  const hasMultipleChoiceTest = multipleChoiceTest && Object.keys(multipleChoiceTest).length > 0
  const displayMultipleChoiceTest = useAppSelector(selectDisplayMultipleChoiceTest)

  const [updateEventsData] = useUpdateEventDataMutation()
  const events = useAppSelector(selectEvents)
  const allCompletedMultipleChoiceTest =
    parentQuestId && events
      ? events.filter(
          (event) =>
            event.status === EventStatus.COMPLETED &&
            event.id.includes(parentQuestId) &&
            event.eventType !== 'answered',
        )
      : []
  const multipleChoiceThreshold = parentQuestId ? getMultipleChoiceThreshold(parentQuestId) : undefined
  const isLastTest =
    hasMultipleChoiceTest && multipleChoiceThreshold
      ? allCompletedMultipleChoiceTest.length + 1 === multipleChoiceThreshold
      : false

  // The "Video Crash Courses" / "Math Vault" boxes are authored into the article HTML itself, and are
  // removed from the markup rather than hidden with CSS, on every surface this view serves. The removal
  // no longer depends on `router.query`, so it runs during static generation and the boxes never reach
  // the served HTML. `Tex` is memoised, so the stripped object is memoised too to keep its identity
  // stable between renders.
  const articleHtmlContent = useMemo(() => ({ html: stripPromoBoxes(htmlContent.html) }), [htmlContent])

  useEffect(() => {
    setEncyclopediaRead(!!isCompleted)
  }, [isCompleted])

  const onMultipleChoiceFinish = (data: MultipleChoiceTestEventScore, mathEnergyOrbs: number): void => {
    const taskId = parentQuestId
    const newEvents = data.map(({ score, stars, points }) => ({
      id: `${taskId}#${indexOfTopic}#${score}`,
      points,
      status: EventStatus.COMPLETED,
      stars,
      eventFeature: REWARD_FEATURE.MATH_ESSENTIALS,
      eventType: REWARD_EVENT.ANSWERED,
    }))

    const eventsWithMathEnergyOrbs = mathEnergyOrbs
      ? [
          ...newEvents,
          {
            id: TentativeEvent.GetMathEnergyOrbs,
            mathEnergyOrbs,
            eventFeature: REWARD_FEATURE.MATH_ESSENTIALS,
            eventType: REWARD_EVENT.ANSWERED,
          },
        ]
      : newEvents

    updateEventsData(eventsWithMathEnergyOrbs)
    if (newEvents.length > 0 && isLastTest && taskId) {
      setMultChoiceProgress({ taskId })
    }
  }

  const handleOnUserCheckedEncyclopedia = (): void => {
    setEncyclopediaRead(true)
    setEncyclopediaProgress({ path })
  }

  const handleOnStartMultipleChoiceTestClick = (): void => {
    dispatch(resetMultipleChoiceTestActionCreator())
    mathEssentialsAnalytics.logButtonClick(`quiz_yourself_${topic.h1}`)
    dispatch(displayMultipleChoiceTestActionCreator(true))
  }

  return (
    <div className="max-w-screen-19/20 md:max-w-3xl">
      {hasMultipleChoiceTest && (
        <MultipleChoiceTestSchema multipleChoiceQuestions={multipleChoiceTest.multipleChoiceQuestions} topic={topic} />
      )}
      <ArticleSchema articleBody={articleBody} topic={topic} />
      <div className="flex flex-col items-center w-full mt-5">
        {hasMultipleChoiceTest && displayMultipleChoiceTest && (
          <MultipleChoiceTest
            indexOfTopic={indexOfTopic}
            multipleChoiceTest={multipleChoiceTest}
            onFinish={onMultipleChoiceFinish}
            parentQuest={parentQuest}
            parentQuestId={parentQuestId}
            topic={topic}
          />
        )}

        {hasMultipleChoiceTest && isMobile && (
          <ContainerHomGradientBorder innerClassName="p-4 bg-opacity-95" outerClassName="mb-4">
            <h6 className="text-xs text-gray-sub-text">{t('encyclopedia:quizAtTheBottomMessage')}</h6>
          </ContainerHomGradientBorder>
        )}

        <div className="flex flex-col space-y-6 w-full">
          <section
            className={`paywall relative lg:m-0 rounded bg-white ${
              !isCurriculumLevel ? 'p-4 md:px-16 md:py-12 lg:px-20 shadow-experimental' : ''
            }`}
          >
            {hasMultipleChoiceTest && !isMobile && (
              <ContainerHomGradientBorderHexagon
                innerClassName="p-6 flex items-center bg-opacity-[0.975]"
                outerClassName="absolute -right-12 -top-16 lg:-right-6 lg:-top-12 w-40 h-40"
              >
                <h6 className="text-center text-xxs text-gray-sub-text">{t('encyclopedia:quizAtTheBottomMessage')}</h6>
              </ContainerHomGradientBorderHexagon>
            )}

            <div className="space-y-6 flex flex-col items-center">
              <Tex cssUrl={cssUrl} fullWidth h1={hideHeading ? '' : h1} htmlContent={articleHtmlContent} />
              {hasMultipleChoiceTest && (
                <div className="flex justify-center">
                  <Button
                    className="border-2 border-purple bg-black-jet px-4 py-2 rounded text-white flex items-center space-x-3"
                    onClick={handleOnStartMultipleChoiceTestClick}
                    size="medium"
                  >
                    <span>{t('encyclopedia:buttonQuizYourself')}</span>
                    <ResponsiveImage
                      alt={t('homebase:space')}
                      height={40}
                      imageClassName="-mt-1.5 -mb-1.5"
                      imageWidths="30"
                      mediaQuery={mediaQuery}
                      srcKey={baseIcon}
                      width={30}
                    />
                  </Button>
                </div>
              )}
            </div>
          </section>

          {authenticated && isEncyclopediaPage && (
            <Checkbox
              checkboxClassName="bg-white h-3 w-3 md:h-5 md:w-5 mt-0.5 border-2 border-purple !rounded-none box-shadow-purple"
              className="mx-auto text-white"
              disabled={isEncyclopediaRead}
              id="encyclopedia"
              isChecked={isEncyclopediaRead}
              isLoading={isLoading}
              onChange={handleOnUserCheckedEncyclopedia}
              text={t('homebase:event.checkQuest')}
            />
          )}

          {!isCurriculumLevel && (
            <div className="grid gap-8 grid-cols-8 items-start">
              {lastTopic && (
                <div className="flex flex-col col-start-1 col-span-4 md:col-span-2 text-center">
                  <ArticleNavigationCard
                    buttonTitle={t('encyclopedia:prevEntry')}
                    description={lastTopic.h1}
                    href={lastTopic.path}
                    navigationDirection="left"
                  />
                </div>
              )}

              {nextTopic && (
                <div className="flex flex-col col-start-5 md:col-start-7 col-span-4 md:col-span-2 text-center">
                  <ArticleNavigationCard
                    buttonTitle={t('encyclopedia:nextEntry')}
                    description={nextTopic.h1}
                    href={nextTopic.path}
                    navigationDirection="right"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
